import { useState } from "react";
import { Link } from "react-router-dom";
import { CloudOff, LocateFixed, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { LABEL, PRIVACY, toUnit, useWeather } from "@/lib/weather";
import WeatherScene from "@/components/weather/WeatherScene";

const SKY = {
  day: { clear: "#38BDF8,#0EA5E9", "partly-cloudy": "#60A5FA,#3B82F6", cloudy: "#94A3B8,#64748B", rain: "#64748B,#334155", drizzle: "#7C8DA6,#475569", thunder: "#475569,#1E293B", snow: "#CBD5E1,#94A3B8", fog: "#CBD5E1,#9CA3AF" },
  night: { clear: "#1E1B4B,#0F172A", "partly-cloudy": "#1E293B,#0F172A", cloudy: "#334155,#1E293B", rain: "#1E293B,#0B1220", drizzle: "#273449,#111827", thunder: "#111827,#030712", snow: "#334155,#1E293B", fog: "#374151,#1F2937" },
};
const hourLabel = (t) => new Date(`${t}:00`).toLocaleTimeString([], { hour: "numeric" });
const dayLabel = (d, i) => (i === 0 ? "Today" : new Date(`${d}T12:00`).toLocaleDateString([], { weekday: "short" }));

function Ask({ allow, deny }) {
  return (
    <div className="q-card flex flex-col gap-3 q-rise" data-testid="weather-location-prompt">
      <p className="flex items-center gap-2 font-display text-lg font-extrabold text-q-text"><LocateFixed className="h-5 w-5" style={{ color: "rgb(var(--glow-calendar))" }} /> Show weather where you are?</p>
      <p className="text-sm text-q-muted" data-testid="weather-privacy-line">{PRIVACY}</p>
      <div className="flex gap-2">
        <button className="q-btn q-btn-primary" onClick={allow} data-testid="weather-allow-btn">Use my location</button>
        <button className="q-btn q-btn-secondary" onClick={deny} data-testid="weather-deny-btn">Use the family's city</button>
      </div>
    </div>
  );
}

function Message({ text, hubId, settings }) {
  return (
    <div className="q-card flex items-center gap-3" data-testid="weather-card-message">
      <CloudOff className="h-6 w-6 text-q-faint" />
      <p className="flex-1 text-sm text-q-muted" data-testid="weather-message">{text}</p>
      {settings && <Link to={`/h/${hubId}/family/settings`} className="q-btn q-btn-secondary min-h-[44px]" data-testid="weather-settings-link">Settings</Link>}
    </div>
  );
}

export default function WeatherCard({ hub }) {
  const { session } = useAuth();
  const { data, asking, allow, deny, setUnit } = useWeather(hub.id, session);
  const [five, setFive] = useState(false);
  if (asking) return <Ask allow={allow} deny={deny} />;
  if (!data) return <div className="q-card h-[188px] animate-pulse" data-testid="weather-card-loading" />;
  if (data.needs_city) return <Message text={data.message} hubId={hub.id} settings={hub.is_parent} />;
  if (data.unavailable) return <Message text={data.message} />;
  const u = data.unit;
  const c = data.current;
  const sky = SKY[c.is_day ? "day" : "night"][c.condition].split(",");
  return (
    <>
      <div className="relative overflow-hidden rounded-2xl text-white q-rise" style={{ background: `linear-gradient(160deg, ${sky[0]}, ${sky[1]})` }} data-testid="weather-card">
        <button className="block w-full p-5 text-left" onClick={() => setFive(true)} data-testid="weather-open-5day" aria-label="Open 5-day forecast">
          <div className="flex items-start gap-4">
            <WeatherScene condition={c.condition} isDay={c.is_day} size={104} testid="weather-scene" />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 truncate text-sm font-semibold text-white/85" data-testid="weather-place"><MapPin className="h-3.5 w-3.5" />{data.place}</p>
              <p className="font-display text-5xl font-extrabold leading-none tabular-nums" data-testid="weather-temp">{toUnit(c.temp_c, u)}°</p>
              <p className="mt-1 text-sm font-semibold" data-testid="weather-condition">{LABEL[c.condition]}</p>
              <p className="text-xs text-white/80" data-testid="weather-feels">Feels like {toUnit(c.feels_c, u)}° · H {toUnit(data.today.hi_c, u)}° L {toUnit(data.today.lo_c, u)}°</p>
              {data.stale && <p className="mt-1 text-xs font-bold text-white/90" data-testid="weather-stale">Updated {new Date(data.updated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>}
            </div>
          </div>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="weather-hourly">
            {data.hourly.map((h, i) => (
              <div key={h.time} className="flex w-12 flex-shrink-0 flex-col items-center gap-0.5 text-xs" data-testid={`weather-hour-${i}`}>
                <span className="text-white/75">{i === 0 ? "Now" : hourLabel(h.time)}</span>
                <WeatherScene condition={h.condition} isDay={h.is_day} size={30} still />
                <span className="font-bold tabular-nums">{toUnit(h.temp_c, u)}°</span>
              </div>
            ))}
          </div>
        </button>
        <div className="absolute right-3 top-3 flex rounded-full bg-black/25 p-0.5 text-xs font-bold backdrop-blur" data-testid="weather-unit-toggle">
          {["F", "C"].map((x) => <button key={x} onClick={() => setUnit(x)} aria-pressed={u === x} data-testid={`weather-unit-${x.toLowerCase()}`} className={`h-11 w-11 rounded-full ${u === x ? "bg-white text-slate-900" : "text-white"}`}>°{x}</button>)}
        </div>
      </div>
      <Dialog open={five} onOpenChange={setFive}>
        <DialogContent className="sm:max-w-sm" data-testid="weather-5day">
          <DialogHeader><DialogTitle>5-day forecast · {data.place}</DialogTitle></DialogHeader>
          <ul className="divide-y divide-q-line">
            {data.daily.map((d, i) => (
              <li key={d.date} className="flex items-center gap-3 py-2" data-testid={`weather-day-${i}`}>
                <span className="w-14 font-semibold text-q-text">{dayLabel(d.date, i)}</span>
                <WeatherScene condition={d.condition} isDay size={34} still />
                <span className="flex-1 text-sm text-q-muted">{LABEL[d.condition]}</span>
                <span className="font-bold tabular-nums text-q-text">{toUnit(d.hi_c, u)}° <span className="text-q-faint">{toUnit(d.lo_c, u)}°</span></span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function WeatherBadge({ hubId }) {
  const { session } = useAuth();
  const { data } = useWeather(hubId, session, { prompt: false });
  if (!data || data.needs_city || data.unavailable) return null;
  return (
    <div className="mt-6 flex items-center gap-3 text-white" data-testid="screensaver-weather">
      <WeatherScene condition={data.current.condition} isDay={data.current.is_day} size={72} testid="screensaver-weather-scene" />
      <div>
        <p className="font-display text-4xl font-extrabold tabular-nums" data-testid="screensaver-weather-temp">{toUnit(data.current.temp_c, data.unit)}°</p>
        <p className="text-sm font-semibold text-white/80" data-testid="screensaver-weather-place">{data.place}</p>
      </div>
    </div>
  );
}
